import { IntervalResource } from '../../typings/Interval';
import { PaginatedResource } from '../../typings/Paginated';
import { BaseService, ClientError, ExtendableError, GenericPaginatedParameters, InvalidValue, NotFound, Unauthorized, UnexpectedError, UserNotInUnit } from './base-service';
import { AlreadySignedReport, RecordNotLatestValues } from './dispatch';

export class AlreadyActiveUnitWithName extends ExtendableError {
    actingBody: string | undefined;
    constructor(code: string, actingBody: string) {
        super(code);
        this.actingBody = actingBody;
    }
}

interface FilteredHistoricParams {
    attachment?: boolean;
    report?: boolean;
    actions?: boolean;
}

interface AppAgentVacanciesPaginatedParameters extends GenericPaginatedParameters {
    actingBodyUnitId?: number;
    actingBodyCommandId?: number;
    workplaceId?: number;
    tagsIds?: number[];
    beginIn?: number;
    endIn?: number;
}

interface ApplicationsResource<TData> {
    applications: TData[];
    applicationsCount: {
        currentMonth: number;
        nextMonth: number;
    };
}

export class InvalidApplication extends ExtendableError {
    message: string;
    constructor(message: string) {
        super(message);
        this.message = message;
    }
}

export class InvalidPermissionToVacancy extends ExtendableError {
    message: string;
    constructor(message: string) {
        super(message);
        this.message = message;
    }
}

export class CannotCancelApplication extends ExtendableError { }

export class AgentIsNotOnTeam extends ExtendableError { }


export class AppAgentService extends BaseService {
    /**
     *
     * @throws {ClientError} CantFinishConcludedSituation
     * @throws {InvalidValue}
     */
    async finishOccurrence({
        occurrenceId,
        report,
        handcuffExplanation,
        category,
        subCategory,
        civilPoliceSupport,
        militaryPoliceSupport,
        rejectPoliceReport,
    }: {
        occurrenceId: number; report: string;
        handcuffExplanation: string; category?: number;
        subCategory?: number;
        civilPoliceSupport: boolean;
        militaryPoliceSupport: boolean;
        rejectPoliceReport: boolean;
    }) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/occurrence/${occurrenceId}/finish`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                report,
                handcuffExplanation,
                category,
                subCategory,
                civilPoliceSupport,
                militaryPoliceSupport,
                rejectPoliceReport
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async createOccurrenceAttachment({ occurrenceId, file, description }: { occurrenceId: number; file: File; description: string; }): Promise<OccurrenceReport> {
        const token = await this.getToken();
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('description', description);

        const res = await fetch(`${this.centralEndpoint}/app-agent/occurrence/${occurrenceId}/attachment`, {
            method: 'POST',
            headers: this.getHeaders(token),
            body: formData,
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOccurrenceById(id: number): Promise<Occurrence> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/occurrence/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOccurrences(): Promise<Occurrence[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/occurrences`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPoliceReports({ occurrenceId }: { occurrenceId: number; }): Promise<OccurrenceReport[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/occurrence/${occurrenceId}/attach-police-report`, {
            method: 'GET',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async attachPoliceReport({ occurrenceId, files, policeReport }: { occurrenceId: number; files: File[]; policeReport: string; }): Promise<void> {
        const token = await this.getToken();

        const formData = new FormData();
        for (const file of files) {
            formData.append('files', file);
        }

        formData.append('policeReport', policeReport);

        const res = await fetch(`${this.centralEndpoint}/app-agent/occurrence/${occurrenceId}/attach-police-report`, {
            method: 'POST',
            headers: this.getHeaders(token),
            body: formData,
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

    }

    async addOccurrenceReport({ occurrenceId, report }: { occurrenceId: number; report: string; }) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/occurrence/${occurrenceId}/report`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ report })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }

            throw new ClientError(resJson.code);
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getOccurrenceVideoUrl(occurrenceId: number): Promise<VideoUrl> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/occurrence/${occurrenceId}/video-url`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getAgentUnits(): Promise<AppAgentUnit[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/agent-units-simplified`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getAssignments(): Promise<DropdownResource[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/agent-assignments`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getAgentSchedules(): Promise<DropdownResource[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/agent-schedules`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getFinishReasons(): Promise<DropdownResource[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/finish-reasons`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSimplifiedVehicleRelations(): Promise<DropdownResource[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/vehicle-relations-simplified`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSimplifiedPersonRelations(): Promise<DropdownResource[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/person-relations-simplified`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getHandcuffReasons(): Promise<DropdownResource[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/handcuff-reasons`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getDropDownProvidences(): Promise<DropdownResource[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/providences-simplified`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSimplifiedObjectCategories(): Promise<ObjectCategory[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/object-categories-simplified`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSimplifiedObjectSubCategories(id: number): Promise<DropdownResource[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/object-sub-categories-simplified/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSimplifiedOccurrenceTypes(): Promise<DropdownResource[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/occurrence-types-simplified`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getAgentActiveUnit(): Promise<AppAgentUnit | null> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/agent-active-unit`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getUnitDisplaceReasonById(displaceReasonId: number): Promise<DisplaceReason> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/displace-reason/${displaceReasonId}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOccurrenceHistoricFiltered(occurrenceId: number, params: FilteredHistoricParams): Promise<OccurrenceReport[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/occurrence/${occurrenceId}/filtered-report-historic${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    /**
     *
     * @throws {ClientError} RequiredToFinishCurrentUnit | AlreadyActiveUnitWithName
     * @returns
     */
    async setAgentActiveUnit(params: {
        unitId: number;
        agents: { id: number; active: boolean; assignmentId?: number; finishEarlyReasonId?: number; agentScheduleId?: number; }[];
        equipments: { id: number; active: boolean; reason?: string; }[];
    }) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/set-agents-unit`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            } else if (resJson.code == 'AlreadyActiveUnitWithName') {
                throw new AlreadyActiveUnitWithName(resJson.code, resJson.actingBody);
            }

            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    /**
     *
     * @returns
     * @throws {ClientError} CannotExitUnitWithOccurrences
     */
    async resetAgentActiveUnit(finishEarlyReasonId?: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/reset-agent-unit`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ finishEarlyReasonId })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new NotFound();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async updateOccurrence(occurrence: {
        id: number;
        involvedVehicles?: InvolvedVehicle[];
        involvedPeople?: InvolvedPerson[];
        involvedObjects?: InvolvedObject[];
        updatedAt: string;
    }) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/occurrence/${occurrence.id}`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(occurrence)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new NotFound();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            } else if (resJson.code == 'RecordNotLatestValues') {
                throw new RecordNotLatestValues(resJson.code, resJson.userName);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async findPersonFromDocument(document: string): Promise<PersonFromDocument> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/cortex/find-from-document/${document}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new NotFound();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async findPersonFromNameAndMotherName(name: string, motherName: string): Promise<PersonFromMotherName[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/cortex/find-from-name${this.encodeQueryParams({ name, motherName })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async findPersonFromNameAndBirthDate(name: string, birthDate: string): Promise<PersonFromBirthDate[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/cortex/find-from-name-and-birth-date${this.encodeQueryParams({ name, birthDate })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async findWarrantFromDocument(document: string): Promise<BasicWarrantInformation[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/cortex/find-warrant-from-document/${document}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new NotFound();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async findWarrantFromNameAndMotherName(name: string, motherName: string): Promise<BasicWarrantInformation[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/cortex/find-warrant-from-name${this.encodeQueryParams({ name, motherName })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async findWarrantFromNameAndBirthDate(name: string, birthDate: string): Promise<BasicWarrantInformation[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/cortex/find-warrant-from-name-and-birth-date${this.encodeQueryParams({ name, birthDate })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async findWarrantMoreInformation(id: number): Promise<CompleteWarrantInformation> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/cortex/find-warrant-by-id/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new NotFound();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async findCarFromPlate(plate: string): Promise<CarFromPlate> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/cortex/find-car-from-plate/${plate}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new NotFound();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async findCarFromChassis(chassis: string): Promise<CarFromChassis[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/cortex/find-car-from-chassis/${chassis}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new NotFound();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getTeamPendingSignatures(): Promise<SignableReport[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/team-pending-signatures`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new NotFound();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async teamSignReport(signableReportId: number, params: { password: string; }) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/team-sign/${signableReportId}`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new NotFound();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'AlreadySignedReport') {
                throw new AlreadySignedReport(resJson.code, resJson.userName);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getSignableReportResultUrl(signableReportId: number): Promise<{ id: number; resultUrl: string; }> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/signable-report/${signableReportId}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new NotFound();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPauseReasons(unitId: number): Promise<PauseReason[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/unit/pause-reasons${this.encodeQueryParams({ unitId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getFinishEarlyReasons(unitId: number): Promise<PauseReason[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/unit/finish-early-reasons${this.encodeQueryParams({ unitId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async pauseUnpauseUnit(id: number, action: 'pause' | 'unpause', pauseReasonId?: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/unit/${id}/pause`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ action, pauseReasonId })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getVacancies(params: Omit<AppAgentVacanciesPaginatedParameters, 'actingBodyId'>): Promise<PaginatedResource<VacancyWithErrors>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/deac/vacancies${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async applyToVacancy(vacancyId: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/deac/vacancy/${vacancyId}/application`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidApplication') {
                throw new InvalidApplication(resJson.message);
            } else if (resJson.code == 'InvalidPermissionToVacancy') {
                throw new InvalidPermissionToVacancy(resJson.message);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getVacancy(vacancyId: number): Promise<{ vacancy: Vacancy; application: Application | undefined; }> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/deac/vacancy/${vacancyId}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidPermissionToVacancy') {
                throw new InvalidPermissionToVacancy(resJson.message);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getApplications(params: { textFilter?: string; month?: number; year?: number; }): Promise<ApplicationsResource<Application>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/deac/applications${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getApplication(id: number): Promise<Application> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/deac/application/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async cancelApplication(id: number, cancellationReasonId: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/deac/application/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                cancellationReasonId
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'CannotCancelApplication') {
                throw new CannotCancelApplication();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async updateAgentServiceStatus(serviceName: Service, status: boolean, reason: string) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/update-service-status`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                serviceName, status, reason
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }
        if (res.status !== 200) {
            throw new UnexpectedError();
        }
    }

    async getAgentLprEvent(detectionId: number): Promise<LprDetection> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/lpr-event/${detectionId}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }
        if (res.status !== 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOwnTeamDetections(): Promise<IntervalResource<LprDetection>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/app-agent/lpr-events`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new AgentIsNotOnTeam();
        }

        if (res.status !== 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getActivitiesAndServices(): Promise<ActivityAndService[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/unit/activities-and-services`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 400) {
            const resJson = await res.json();
            if (resJson.code == 'UserNotInUnit') {
                throw new UserNotInUnit();
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getActivityAndServiceById(id: number): Promise<ActivityAndService> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/unit/activity-and-service/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 400) {
            const resJson = await res.json();
            if (resJson.code == 'UserNotInUnit') {
                throw new UserNotInUnit();
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSimplifiedPatrolIndicators(): Promise<PatrolIndicator[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/simplified-patrol-indicators`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 400) {
            const resJson = await res.json();
            if (resJson.code == 'UserNotInUnit') {
                throw new UserNotInUnit();
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createActivityAndService(params: CreateActivityAndService): Promise<ActivityAndService> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/unit/activity-and-service`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                ...params,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateActivityAndService(params: UpdateActivityAndService): Promise<ActivityAndService> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/unit/activity-and-service/${params.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                ...params,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 400) {
            const resJson = await res.json();
            if (resJson.code == 'UserNotInUnit') {
                throw new UserNotInUnit();
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteActivityAndService(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/app-agent/unit/activity-and-service/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 400) {
            const resJson = await res.json();
            if (resJson.code == 'UserNotInUnit') {
                throw new UserNotInUnit();
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }
}

export const appAgentService = new AppAgentService();
